import { Tooltip as MaterialTailwindTooltip } from '@material-tailwind/react';
import { ReactNode, ComponentProps } from 'react';

type TooltipProps = Omit<
  ComponentProps<typeof MaterialTailwindTooltip>,
  'className' | 'content'
> & {
  content?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  variant?: 'primary' | 'secondary';
  size?: 'md' | 'sm' | 'xs';
  showDelay?: number;
  as?: 'div' | 'span';
  enabled?: boolean;
};

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  className = '',
  contentClassName = '',
  variant = 'primary',
  size = 'md',
  placement = 'top',
  showDelay = 0.3,
  as = 'div',
  enabled = true,
  ...rest
}) => {
  if (!content) {
    return <>{children}</>;
  }

  const baseClasses = ['rounded', 'font-normal', 'z-[10002]'];

  const variantClasses = {
    primary: 'bg-gray-800 text-white',
    secondary: 'bg-white text-gray-800',
  };

  const sizeClasses = {
    md: 'py-2 px-4 text-md',
    sm: 'py-1 px-3 text-sm',
    xs: 'py-0.5 px-2 text-xs',
  };

  const classes = [
    ...baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    enabled ? '' : 'hidden',
    className,
  ].join(' ');

  return (
    <MaterialTailwindTooltip
      content={content}
      placement={placement}
      className={classes}
      animate={{
        mount: { opacity: 1, transition: { delay: showDelay } },
        unmount: { opacity: 0, transition: { duration: 0.1 } },
      }}
      offset={5}
      {...rest}
    >
      {as === 'span' ? (
        <span className={`inline-flex max-w-full ${contentClassName}`}>
          {children}
        </span>
      ) : (
        <div className={`flex max-w-full ${contentClassName}`}>{children}</div>
      )}
    </MaterialTailwindTooltip>
  );
};

export default Tooltip;
